import Link from 'next/link'
import Image from 'next/image'
import { PATHS } from '@/fractals/constants'
import { sortBasedOnKey } from '@/fractals/utils'
import { getFooterData } from '@/modules/shared/widgets'
import { IMG_LICENCE_NUMBER } from '@/modules/shared/widgets/Footer/fractals'
import { Logo } from '~shared/components/logo'
import { cn } from '~shared/utils/cn'
// todo: to public/admin

import { SubscriberForm } from './subscriber-form'
import { useIsEdpartnersLead } from '~shared/hooks/useIsEdpartnersLead'
import { TelegramIcon } from '~shared/icons/telegram'
import { VkIcon } from '~shared/icons/vk'
import { replacePrepositionsWhitespaces } from '~shared/utils/replace-prepositions-whitespaces'

const PhoneCard = ({
  phone,
  title,
  className
}: {
  phone: string
  title: string
  className?: string
}) => (
  <div className={cn('font-medium', className)}>
    <p className='text-[10px] text-muted'>{title}</p>
    <a
      href={`tel:${phone.replace(/[^\d+]/g, '')}`}
      className='block mt-2 text-[12px] xl:text-[14px]'>
      {phone}
    </a>
  </div>
)

export interface FooterProps {
  className?: string
  data: Awaited<ReturnType<typeof getFooterData>>
}

export const Footer = (props: FooterProps) => {
  const { className, data, ...rest } = props

  // todo: refactor translations
  const imgLicenseNumber = IMG_LICENCE_NUMBER
  const imgLicenseTitle = 'Государственная лицензия'
  const countryNumber = '+7 (800) 444-81-38'
  const capitalNumber = '+7 (495) 648-62-26'
  const address = 'г. Москва, Дербеневская набережная, д. 11'
  const companyName = 'Московский Институт Технологий и Управления'
  const currentYear = new Date().getFullYear()

  const urls = [
    {
      name: 'Программы',
      items:
        data?.categories
          ?.map(category => ({
            ...category,
            slug: (category.slug && `/${category.slug}`) || null
          }))
          ?.filter(category => category?.slug) || null
    },
    {
      name: 'Направления',
      items:
        data?.faculties
          ?.map(faculty => ({
            ...faculty,
            slug: `${PATHS.front.general.programs.ru}${
              faculty?.slug
                ? `--${PATHS.front.general.faculty.ru}--${faculty?.slug}`
                : ''
            }`
          }))
          ?.filter(faculty => faculty?.slug) || null
    },
    {
      name: 'Об институте',
      items: sortBasedOnKey(
        [
          {
            name: 'Контакты',
            slug: PATHS.front.contact.index,
            rank: 1
          }
        ],
        'rank'
      )
    }
  ]

  const isEdpartnersLead = useIsEdpartnersLead()

  return (
    <footer
      className={cn('bg-tertiary', className)}
      {...rest}
      itemScope
      itemType='http://schema.org/WPFooter'>
      <meta itemProp='copyrightYear' content={`${currentYear}`} />
      <meta itemProp='copyrightHolder' content={companyName} />
      <div
        className='container px-[16px] md:px-[64px] lg:px-[112px] xl:px-[96px] pt-20'
        itemScope
        itemType='http://schema.org/Organization'>
        <Logo id='footer' className='text-primary' />

        <div className='grid xl:grid-cols-2 grid-cols-1 mt-16'>
          <div className='grid md:grid-cols-3 grid-cols-2 gap-4'>
            {urls.map(item => (
              <div
                itemScope
                itemType='https://schema.org/SiteNavigationElement'
                key={item.name}>
                <p className='font-medium text-[20px] leading-[1.2] mb-2 text-primary'>
                  {item.name}
                </p>
                <ul className='flex flex-col gap-6 mt-[24px] lg:mt-[32px]'>
                  {item.items?.map(link => (
                    <li key={link.name}>
                      <Link
                        prefetch={false}
                        href={link.slug || '#'}
                        className='text-muted text-[14px] lg:text-[16px] leading-[1.2]'>
                        {replacePrepositionsWhitespaces(link.name)}
                      </Link>
                    </li>
                  ))}
                  {item.name === 'Программы' && (
                    <li>
                      <Link
                        prefetch={false}
                        href='/programmy'
                        className='text-muted text-[14px] lg:text-[16px] leading-[1.2]'>
                        Все программы
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          <div className='mt-[40px] md:mt-[56px] xl:mt-0 flex flex-col md:flex-row xl:flex-col xl:pl-[120px] text-primary'>
            <div className='xl:order-1 sm:order-2 basis-auto md:basis-1/2 xl:basis-auto mt-[36px] sm:mt-[50px] lg:mt-0'>
              <p className='text-[14px] lg:text-[16px] font-medium'>
                {address}
              </p>
              {!isEdpartnersLead && (
                <div className='flex gap-20 mt-6'>
                  <PhoneCard
                    title='Бесплатно по Москве'
                    phone={capitalNumber}
                  />
                  <PhoneCard
                    title='Бесплатно по России'
                    phone={countryNumber}
                  />
                </div>
              )}
            </div>
            <div className='xl:order-2 sm:order-1 basis-auto md:basis-1/2 xl:basis-auto'>
              <p className='font-medium text-[20px] xl:mt-16'>
                {imgLicenseTitle}
              </p>
              <div className='mt-[24px] lg:mt-[32px] flex gap-6'>
                <Image
                  src='/assets/imgs/docs/license.jpg'
                  height={144}
                  width={104}
                  alt={imgLicenseTitle}
                  className='rounded-[8px]'
                />
                <span className='font-medium text-input-placeholder'>
                  {imgLicenseNumber}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 gap-[40px] md:grid-cols-[1fr,auto] lg:grid-cols-2 mt-[40px] md:mt-[56px]'>
          <SubscriberForm />
          <div className='xl:pl-[120px] h-12 items-center flex gap-[54px] text-primary md:mt-auto md:justify-self-end xl:justify-self-start'>
            <Link href='https://t.me/mitm_institute'>
              <TelegramIcon className='size-6' />
            </Link>
            <Link href='https://vk.com/mitm.institute'>
              <VkIcon className='size-6' />
            </Link>
          </div>
        </div>

        <div className='flex justify-center border-t border-muted text-muted text-[14px] py-6 mt-[40px] md:mt-[56px]'>
          <span>
            &copy;&nbsp;{companyName},&nbsp;{currentYear}
          </span>
          {/* <span>Политика кофиденциальности</span>
          <span>Пользовательское соглашение</span> */}
        </div>
      </div>
    </footer>
  )
}
